import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Search, Bell, Menu, Settings, X } from "lucide-react"
import { Button } from "components/ui/button"
import { SettingsModal, type CurrentUser } from "components/modals/SettingsModal"
import { NotificationsModal } from "components/modals/NotificationsModal"
import { getUnreadCount } from "services/notifications"
import { globalSearch } from "services/globalSearch"
import { GlobalSearchResults } from "./GlobalSearchResults"

// Mappatura route -> titoli pagina
const PAGE_TITLES: Record<string, string> = {
  '/dashboard-new': 'Dashboard',
  '/contacts': 'Contatti',
  '/tasks': 'Task',
  '/agenda': 'Agenda',
  '/team': 'Team',
  '/leads': 'Leads',
    '/mail/accounts': 'Caselle mail',
    '/mail/inbox': 'Posta',
    '/mail/compose': 'Scrivi',
    '/mail/drafts': 'Bozze',
}

interface TopBarProps {
  currentUser?: CurrentUser | null
  onUserUpdate?: (user: CurrentUser) => void
  onMenuOpen?: () => void
}

export function TopBar({ currentUser, onUserUpdate, onMenuOpen }: TopBarProps) {
  const location = useLocation()
  const pageTitle = location.pathname.startsWith('/documents') ? 'Documenti' : PAGE_TITLES[location.pathname] || 'Dashboard'

  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState({ contacts: [], tasks: [], events: [], leads: [] })
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Polling unread count ogni 15 secondi — solo se c'è un utente autenticato
  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0)
      return
    }

    const fetchUnreadCount = async () => {
      try {
        const count = await getUnreadCount()
        setUnreadCount(count)
      } catch (error) {
        console.error("Error loading unread count:", error)
      }
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 15000)

    return () => clearInterval(interval)
  }, [currentUser])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (searchQuery.trim().length < 2) {
      setSearchResults({ contacts: [], tasks: [], events: [], leads: [] })
      setShowResults(false)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    setShowResults(true)

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await globalSearch(searchQuery)
        setSearchResults(results)
      } catch (err) {
        console.error("Global search error:", err)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchQuery])

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault()
        setSearchOpen((prev) => {
          if (prev) {
            setSearchQuery("")
            setShowResults(false)
          }
          return !prev
        })
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    setSearchQuery("")
    setShowResults(false)
    setSearchResults({ contacts: [], tasks: [], events: [], leads: [] })
  }, [])

  const handleNotificationsClose = () => {
    setNotificationsModalOpen(false)
    // Refresh immediato del conteggio dopo la chiusura del modale
    getUnreadCount()
      .then((count) => setUnreadCount(count))
      .catch(() => {})
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
  }

  return (
    <header className="h-12">
      <div className="h-full px-3 sm:px-6 flex items-center justify-between">
        {/* Page title or search */}
        {searchOpen ? (
          <div ref={searchContainerRef} className="flex-1 min-w-0 max-w-md relative">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca contatti, task, eventi..."
                autoFocus
                className="flex-1 min-w-0 rounded-lg px-3 py-1.5 focus:outline-none"
                style={{
                  backgroundColor: "var(--hexa-topbar-search-bg)",
                  border: "1px solid var(--hexa-topbar-search-border)",
                  color: "var(--hexa-text-primary)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--hexa-topbar-search-focus)"
                  if (searchQuery.trim().length >= 2) setShowResults(true)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeSearch()
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeSearch}
                style={{ color: "var(--hexa-text-secondary)" }}
              >
                <X className="w-4 h-4" />
              </Button>
            </form>
            {showResults && searchQuery.trim().length >= 2 && (
              <GlobalSearchResults
                results={searchResults}
                isSearching={isSearching}
                query={searchQuery}
                onClose={closeSearch}
              />
            )}
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Apri navigazione principale" onClick={onMenuOpen}>
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="truncate text-lg font-semibold" style={{ color: "var(--hexa-text-primary)" }}>
              {pageTitle}
            </h1>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-1.5">
          {/* Search */}
          {!searchOpen && (
            <Button
              variant="ghost"
              size="icon"
              className="transition-colors"
              style={{
                color: "var(--hexa-text-primary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--hexa-topbar-icon-hover)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent"
              }}
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="w-4 h-4" />
            </Button>
          )}

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="transition-colors relative"
            style={{
              color: "var(--hexa-text-primary)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--hexa-topbar-icon-hover)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent"
            }}
            aria-label="Notifications"
            onClick={() => setNotificationsModalOpen(true)}
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] flex items-center justify-center"
                style={{
                  backgroundColor: "var(--hexa-error)",
                  color: "#ffffff",
                }}
              >
                {unreadCount}
              </span>
            )}
          </Button>

          {/* Settings */}
          <Button
            variant="ghost"
            size="icon"
            className="transition-colors"
            style={{
              color: "var(--hexa-text-primary)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--hexa-topbar-icon-hover)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent"
            }}
            aria-label="Settings"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Notifications Modal */}
      <NotificationsModal
        isOpen={notificationsModalOpen}
        onClose={handleNotificationsClose}
        onUnreadCountChange={setUnreadCount}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onUserUpdate={onUserUpdate}
      />
    </header>
  )
}
